/**
 * AdminCareNeeds — JCC Admin v1 Care Needs review queue.
 *
 * Login uses classic /api/auth/login (JWT). ProtectedRoute (admin) is
 * applied at the route in App.js — unchanged from before this build.
 *
 * Reuses the existing backend wherever possible: approve /
 * request-changes / reject / admin-notes / status_history are all
 * pre-existing endpoints. New in this build: pause / resume /
 * mark-filled / archive / unarchive admin actions, plus the
 * status_group / needs_action / submission_kind / family_name /
 * care_type_label fields the backend now computes and returns.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, AlertCircle, CheckCircle2, PauseCircle,
  Archive, ArchiveRestore, RotateCcw, Mail, MailWarning, MailQuestion,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { PageHero } from "../../components/shared/Primitives";

const TABS = [
  { key: "needs_action", label: "Needs action" },
  { key: "all", label: "All" },
  { key: "changes_requested", label: "Changes requested" },
  { key: "approved", label: "Approved" },
  { key: "paused", label: "Paused" },
  { key: "filled_closed", label: "Filled / closed" },
  { key: "declined", label: "Declined" },
  { key: "archived", label: "Archived" },
];

function matchesTab(post, tab) {
  if (post.status === "draft") return false; // drafts are the family's private scratchpad
  if (tab === "all") return true;
  if (tab === "needs_action") return !!post.needs_action;
  return post.status_group === tab;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Turns a stored status_history row into a plain-English activity line.
// Uses only real stored data (from/to/at/notes) — no fabricated steps.
function historyLabel(h) {
  const to = h.to;
  if (h.from === null && to === "draft") return "Draft created";
  if (to === "pending_review") return h.from === "changes_requested" ? "Resubmitted" : "Submitted";
  if (to === "changes_requested") return "Changes requested";
  if (to === "approved") return "Approved";
  if (to === "paused") return "Paused";
  if (to === "matched") return "Marked filled";
  if (to === "closed") return "Closed by family";
  if (to === "rejected") return "Unable to publish";
  if (to === "archived") return "Archived";
  return to || "Updated";
}

const INTEREST_STATUS_LABELS = {
  submitted: "Awaiting family response",
  accepted: "Introduction accepted",
  declined: "Not progressed",
};
const INTEREST_STATUS_STYLES = {
  submitted: "bg-purple-100 text-purple-800",
  accepted: "bg-emerald-100 text-emerald-800",
  declined: "bg-slate-100 text-slate-500",
};

// Fixed to a couple of decisions:
// 'sent' -> accepted by Resend (NOT a delivery/inbox confirmation — Resend
// webhooks aren't wired up yet, see admin report), 'failed' -> Resend
// rejected/errored, 'skipped' -> no key/recipient configured, null ->
// pre-dates this field. Never a security signal — purely so admin/family
// can see whether an email was actually attempted instead of guessing.
function EmailStatusPill({ label, status }) {
  const map = {
    sent: { cls: "bg-emerald-100 text-emerald-800", Icon: Mail, text: "sent" },
    failed: { cls: "bg-red-100 text-red-800", Icon: MailWarning, text: "failed" },
    skipped: { cls: "bg-slate-100 text-slate-500", Icon: MailQuestion, text: "skipped" },
    skipped_retroactive: { cls: "bg-slate-100 text-slate-400", Icon: MailQuestion, text: "Legacy post—approval email not sent by this feature" },
  };
  const cfg = map[status] || { cls: "bg-slate-100 text-slate-400", Icon: MailQuestion, text: "unknown" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" /> {label} {cfg.text}
    </span>
  );
}

function fmtDateTimeShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Read-only oversight of every expression of interest on this post —
// gives admin the same visibility as the family (carer name, status,
// timestamps) plus email delivery status for all three notifications
// triggered by "I'm interested" (family / carer / admin).
function AdminInterestsPanel({ postId }) {
  const [interests, setInterests] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      setInterests(null);
      try {
        const { data } = await api.get(`/api/care-needs/${postId}/interests`);
        if (alive) setInterests(data.interests || []);
      } catch { if (alive) setInterests([]); }
    })();
    return () => { alive = false; };
  }, [postId]);

  return (
    <div className="border-t pt-3" data-testid={`admin-cn-interests-${postId}`}>
      <div className="text-sm font-semibold mb-2">Expressions of interest</div>
      {interests === null ? (
        <p className="text-xs text-ink-muted">Loading…</p>
      ) : interests.length === 0 ? (
        <p className="text-xs text-ink-muted">No carer has expressed interest yet.</p>
      ) : (
        <ul className="space-y-2">
          {interests.map((i) => (
            <li key={i.interest_id} className="rounded-lg border border-slate-200 p-2.5" data-testid={`admin-cn-interest-${i.interest_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm">{i.carer_name}</span>
                <span className={`pill text-xs ${INTEREST_STATUS_STYLES[i.status] || "bg-slate-100"}`}>
                  {INTEREST_STATUS_LABELS[i.status] || i.status}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-ink-muted">
                Submitted {fmtDateTimeShort(i.created_at)}
                {i.responded_at ? ` · Responded ${fmtDateTimeShort(i.responded_at)}` : ""}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <EmailStatusPill label="Family" status={i.family_notify_status} />
                <EmailStatusPill label="Carer" status={i.carer_notify_status} />
                <EmailStatusPill label="Admin" status={i.admin_notify_status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminCareNeeds() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("needs_action");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [searchParams] = useSearchParams();

  const load = useCallback(async (preserveSelection = true) => {
    try {
      const { data } = await api.get("/api/admin/care-needs");
      const all = data.posts || [];
      setPosts(all);
      setLoaded(true);
      if (preserveSelection && selected) {
        const still = all.find((p) => p.post_id === selected.post_id);
        setSelected(still || null);
      }
      return all;
    } catch (e) { toast.error("Could not load review queue."); return []; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Initial load + deep-link support (?post=<id> opens straight into
  // that submission and switches to the matching tab).
  useEffect(() => {
    (async () => {
      const all = await load(false);
      const deepLinkId = searchParams.get("post");
      if (deepLinkId) {
        const found = all.find((p) => p.post_id === deepLinkId);
        if (found) {
          setSelected(found);
          setTab(found.status === "draft" ? "all" : (found.needs_action ? "needs_action" : found.status_group));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => posts.filter((p) => matchesTab(p, tab)), [posts, tab]);
  const counts = useMemo(() => {
    const c = {};
    TABS.forEach(({ key }) => { c[key] = posts.filter((p) => matchesTab(p, key)).length; });
    return c;
  }, [posts]);

  const act = async (path, body, confirmMsg) => {
    if (!selected) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/admin/care-needs/${selected.post_id}/${path}`, body || {});
      toast.success("Done.");
      setFeedback(""); setAdminNote("");
      setSelected(data);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Action failed."); }
    finally { setBusy(false); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      await api.patch(`/api/admin/care-needs/${selected.post_id}/admin-notes`,
                      { admin_notes: selected.admin_notes || "" });
      toast.success("Admin notes saved.");
    } catch (e) { toast.error("Could not save notes."); }
  };

  return (
    <div data-testid="page-admin-care-needs">
      <PageHero eyebrow="Admin" title="Care Needs review queue"
                description="See what needs your attention, what's happened so far, and act on each submission.">
        <Link to="/admin" className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="admin-cn-back-home">
          <ArrowLeft className="w-4 h-4" /> Admin home
        </Link>
      </PageHero>
      <section className="section pt-8">
        <div className="container-jcc max-w-6xl">
          <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-cn-tabs">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { setTab(key); }}
                data-testid={`admin-cn-tab-${key}`}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${
                  tab === key ? "bg-purple text-white border-purple" : "border-slate-200 text-ink-muted hover:border-purple/40"
                }`}
              >
                {label}
                {counts[key] > 0 && (
                  <span className={`ml-1.5 ${tab === key ? "text-white/80" : "text-ink-muted/70"}`}>({counts[key]})</span>
                )}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <ul className="space-y-2" data-testid="admin-cn-list">
              {loaded && filtered.length === 0 && (
                <li className="text-ink-muted text-sm py-6 text-center border border-dashed border-slate-200 rounded-xl">
                  Nothing in this view.
                </li>
              )}
              {filtered.map((p) => (
                <li key={p.post_id}>
                  <button
                    className={`w-full text-left rounded-xl border p-3.5 hover:bg-slate-50 transition-colors ${
                      selected?.post_id === p.post_id ? "border-purple bg-purple/5" : p.needs_action ? "border-amber-300 bg-amber-50/50" : "border-slate-200"
                    }`}
                    onClick={() => setSelected(p)}
                    data-testid={`admin-cn-select-${p.post_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate">{p.family_name || p.owner_email}</span>
                      {p.needs_action && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`admin-cn-badge-${p.post_id}`}>
                          <AlertCircle className="w-3 h-3" />
                          {p.submission_kind === "resubmitted" ? "Resubmitted" : "New"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-muted mt-0.5">
                      {p.location_area || "Location not set"} · {p.care_type_label || "Care type not set"}
                    </div>
                    <div className="flex items-center justify-between mt-1.5 text-xs text-ink-muted">
                      <span>{p.status_label_family}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(p.updated_at)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            <div>
              {!selected ? (
                <p className="text-ink-muted">Select a submission to review.</p>
              ) : (
                <div className="card-base p-5 space-y-4 text-sm" data-testid="admin-cn-detail">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg font-semibold">{selected.family_name || selected.owner_email}</div>
                      <div className="text-ink-muted text-xs">{selected.owner_email}</div>
                    </div>
                    <span className="pill bg-slate-100 text-ink shrink-0">{selected.status_label_family}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm border-y border-slate-100 py-3">
                    <div><span className="text-ink-muted text-xs block">Submitted</span>{fmtDate(selected.submitted_at)}</div>
                    <div><span className="text-ink-muted text-xs block">Last activity</span>{fmtDate(selected.updated_at)}</div>
                    <div><span className="text-ink-muted text-xs block">Location</span>{selected.location_area || "—"}</div>
                    <div><span className="text-ink-muted text-xs block">Care type</span>{selected.care_type_label || "—"}</div>
                    <div><span className="text-ink-muted text-xs block">Proposed start</span>{selected.start_date ? fmtDate(selected.start_date) : (selected.preferred_start_timing || "—")}</div>
                    <div><span className="text-ink-muted text-xs block">Duration / hours</span>{selected.duration_band || "—"} · {selected.hours_per_week_band || "—"}</div>
                  </div>

                  {/* Connection-process visibility (2026-10-03) — facts only,
                      no inference about whether a carer has been "hired".
                      Approval email / interest counts / Care Room link /
                      closure outcome are each shown as independent facts. */}
                  <div className="grid grid-cols-2 gap-3 text-sm border-b border-slate-100 pb-3" data-testid="admin-cn-connection-facts">
                    <div>
                      <span className="text-ink-muted text-xs block">First published</span>
                      {fmtDate(selected.approval_email_sent_at)}
                    </div>
                    <div>
                      <span className="text-ink-muted text-xs block mb-0.5">Approval email</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        {selected.approval_email_status
                          ? <EmailStatusPill label="" status={selected.approval_email_status} />
                          : "—"}
                        {selected.approval_email_status === "failed" && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => act("retry-approval-email")}
                            className="text-[11px] text-teal hover:underline font-medium"
                            data-testid="admin-cn-retry-approval-email"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-ink-muted text-xs block">Carer interest</span>
                      {selected.interest_total_count > 0
                        ? `${selected.interest_total_count} total · latest ${fmtDateTimeShort(selected.latest_interest_at)}`
                        : "None yet"}
                    </div>
                    <div>
                      <span className="text-ink-muted text-xs block">Care Discussion Room</span>
                      {selected.has_linked_care_room
                        ? "Care Room exists for this placement*" : "Not created yet"}
                    </div>
                    {selected.status === "closed" && (
                      <>
                        <div><span className="text-ink-muted text-xs block">Closed</span>{fmtDate(selected.closed_at)}</div>
                        <div><span className="text-ink-muted text-xs block">Family-reported outcome</span>{selected.family_reported_outcome_label || "Not provided"}</div>
                      </>
                    )}
                    {selected.has_linked_care_room && (
                      <div className="col-span-2 text-[11px] text-ink-soft">
                        *A Care Room exists for this family's placement. This does NOT confirm it originated
                        from this specific Care Need — no room stores a direct link back to a post today, so
                        it may be a different, unrelated room for the same care recipient.
                      </div>
                    )}
                    <div className="col-span-2 text-[11px] text-ink-soft">
                      Email statuses show acceptance by our email provider, not confirmed inbox delivery — see the Admin report for details.
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div><strong>Funding:</strong> {selected.funding_type || "—"} · <strong>Budget:</strong> {selected.budget_band || "—"}</div>
                    <div><strong>Rota:</strong> {selected.rota_pattern || "—"}</div>
                    <div><strong>Relationship:</strong> {selected.relationship_to_recipient || "—"}</div>
                    <div><strong>Essentials:</strong> {(selected.essential_requirements || []).join(", ") || "—"}</div>
                    <div><strong>Support needs:</strong><br/><span className="whitespace-pre-wrap">{selected.support_needs || "—"}</span></div>
                    <div><strong>Night expectations:</strong><br/><span className="whitespace-pre-wrap">{selected.night_expectations || "—"}</span></div>
                    <div><strong>Other:</strong><br/><span className="whitespace-pre-wrap">{selected.additional_details || "—"}</span></div>
                    {selected.relisted_from_post_id && (
                      <div className="text-xs bg-slate-50 border p-2 rounded">Relisted from post {selected.relisted_from_post_id}</div>
                    )}
                  </div>

                  {selected.status !== "draft" && <AdminInterestsPanel postId={selected.post_id} />}

                  <label className="block">
                    <span className="text-sm font-medium">Admin notes (private)</span>
                    <textarea value={selected.admin_notes || ""} rows={3}
                              onChange={(e) => setSelected({ ...selected, admin_notes: e.target.value })}
                              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                              data-testid="admin-cn-notes"/>
                    <Button size="sm" variant="outline" onClick={saveNotes} className="mt-1">Save notes</Button>
                  </label>

                  {selected.status === "pending_review" && (
                    <div className="border-t pt-3 space-y-2">
                      <label className="block">
                        <span className="text-sm font-medium">Feedback to family (visible)</span>
                        <textarea value={feedback} rows={2}
                                  onChange={(e) => setFeedback(e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                                  data-testid="admin-cn-feedback"/>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <Button disabled={busy} onClick={() => act("approve", { admin_note: adminNote })}
                                data-testid="admin-cn-approve">Approve</Button>
                        <Button disabled={busy || !feedback} variant="outline"
                                onClick={() => act("request-changes", { family_feedback_note: feedback, admin_note: adminNote })}
                                data-testid="admin-cn-request-changes">Request changes</Button>
                        <Button disabled={busy || !feedback} variant="ghost"
                                onClick={() => act("reject", { family_feedback_note: feedback, admin_note: adminNote })}
                                data-testid="admin-cn-reject">Unable to publish</Button>
                      </div>
                    </div>
                  )}

                  {selected.status === "rejected" && (
                    <div className="border-t pt-3">
                      <label className="block">
                        <span className="text-sm font-medium">Return for changes (feedback required)</span>
                        <textarea value={feedback} rows={2} onChange={(e) => setFeedback(e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"/>
                      </label>
                      <Button disabled={!feedback || busy} size="sm"
                              onClick={() => act("return-for-changes", { family_feedback_note: feedback })}
                              data-testid="admin-cn-return-for-changes">
                        Return for changes
                      </Button>
                    </div>
                  )}

                  {/* Admin action controls added for JCC Admin v1 */}
                  <div className="border-t pt-3 flex flex-wrap gap-2">
                    {selected.status === "approved" && (
                      <>
                        <Button disabled={busy} size="sm" variant="outline" onClick={() => act("pause")} data-testid="admin-cn-pause">
                          <PauseCircle className="w-3.5 h-3.5" /> Pause
                        </Button>
                        <Button disabled={busy} size="sm" variant="outline" onClick={() => act("mark-filled")} data-testid="admin-cn-mark-filled">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark filled / closed
                        </Button>
                      </>
                    )}
                    {selected.status === "paused" && (
                      <>
                        <Button disabled={busy} size="sm" variant="outline" onClick={() => act("resume")} data-testid="admin-cn-resume">
                          <RotateCcw className="w-3.5 h-3.5" /> Resume
                        </Button>
                        <Button disabled={busy} size="sm" variant="outline" onClick={() => act("mark-filled")} data-testid="admin-cn-mark-filled">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark filled / closed
                        </Button>
                      </>
                    )}
                    {selected.status === "archived" ? (
                      <Button disabled={busy} size="sm" variant="outline" onClick={() => act("unarchive")} data-testid="admin-cn-unarchive">
                        <ArchiveRestore className="w-3.5 h-3.5" /> Unarchive
                      </Button>
                    ) : selected.status !== "draft" && (
                      <Button disabled={busy} size="sm" variant="ghost"
                              onClick={() => act("archive", null, "Archive this submission? It will leave the active queue but its full history is kept and it can be unarchived later.")}
                              data-testid="admin-cn-archive">
                        <Archive className="w-3.5 h-3.5" /> Archive
                      </Button>
                    )}
                  </div>

                  {/* Activity history — renders the backend's existing
                      status_history, not a new/duplicate history system. */}
                  <div className="border-t pt-3">
                    <div className="text-sm font-semibold mb-2">Activity history</div>
                    <ol className="space-y-2" data-testid="admin-cn-history">
                      {(selected.status_history || []).map((h, i) => (
                        <li key={i} className="text-xs border-l-2 border-slate-200 pl-3">
                          <span className="font-medium text-ink">{historyLabel(h)}</span>
                          <span className="text-ink-muted"> — {fmtDateTime(h.at)}</span>
                          {h.family_feedback_note && (
                            <div className="text-ink-muted mt-0.5">Feedback to family: &ldquo;{h.family_feedback_note}&rdquo;</div>
                          )}
                          {h.admin_note && (
                            <div className="text-ink-muted mt-0.5">Admin note: &ldquo;{h.admin_note}&rdquo;</div>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
